import React, { useState } from "react"
import { Link } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Label,
  Form,
  Alert,
  Input,
  FormFeedback,
  Button,
} from "reactstrap"
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"
import * as Yup from "yup"
import { useFormik } from "formik"
import withRouter from "components/Common/withRouter"
import MailOutlineIcon from "@mui/icons-material/MailOutline"
import PasswordIcon from "@mui/icons-material/Password"
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import SnackbarComponent from "components/SnackBar"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import { IntroScreen } from "./intro-screen"
import "../../newcomponents/index.css"

// actions
import { loginUser } from "../../store/actions"
import config from "config/config"
import axios from "axios"
import PrimaryButton from "newcomponents/primary-button"

const Login = props => {
  document.title = "Login | AeraVue"

  const dispatch = useDispatch()
  const [show2fa, setShow2fa] = useState(false)
  const [loginData, setLoginData] = useState(null)
  const [twofaCode, setTwofaCode] = useState("")
  const [twofaError, setTwofaError] = useState("")
  const [err, setErr] = useState(null)
  const [success, setSuccess] = useState(null)
  const [showPassword, setShowPassword] = useState(false)

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: async values => {
      const response = await axios.get(
        `${config.backendUrl}/user/getUserByEmail?email=${values.email}`,
      )
      const { user } = response.data

      if (user && !user.isActive) {
        setErr(
          "Your access has been disabled, contact admin for further details.",
        )
        return
      }

      if (user && user.twoFactorEnabled) {
        setShow2fa(true)
        setLoginData(values)

        if (user.twoFactorMethod === "Email") {
          await axios.post(`${config.backendUrl}/auth/sendMFA`, {
            userId: user.id,
          })
        }
      } else {
        dispatch(loginUser(values, props.router.navigate))
      }
    },
  })

  const selectLoginState = state => state.Login
  const LoginProperties = createSelector(selectLoginState, login => ({
    error: login.error,
    userData: login.userData,
  }))
  const { error } = useSelector(LoginProperties)

  const handle2faSubmit = async e => {
    e.preventDefault()
    setTwofaError("")

    if (!twofaCode) {
      setTwofaError("Please enter your 2FA code.")
      return
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || config.backendUrl}/auth/2fa/verify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${loginData?.token}`,
          },
          body: JSON.stringify({ code: twofaCode, email: loginData?.email }),
        },
      )

      const result = await response.json()

      if (response.status === 200 && result.userInfo) {
        setShow2fa(false)
        dispatch(loginUser(loginData, props.router.navigate))
      } else {
        setTwofaError(result.message || "Invalid 2FA code.")
      }
    } catch (err) {
      setTwofaError("Error verifying 2FA code.")
    }
  }

  return (
    <React.Fragment>
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />

      <Container
        fluid
        className="min-vh-100 d-flex flex-column primary-bg"
        style={{
          backgroundImage: `url('/circle1.svg'), url('/circle2.svg')`,
          backgroundPosition: "bottom left, top right",
          backgroundRepeat: "no-repeat, no-repeat",
          backgroundSize: "24%, 24%",
        }}
      >
        <Row className="flex-grow-1">
          {/* Left Side Intro (hidden on mobile) */}
          <Col lg={6} className="d-none d-lg-flex align-items-center p-0">
            <IntroScreen
              title1="Reputation You Can See,"
              title2="Results you can trust."
              subtitle="Built for trust-driven businesses."
            />
          </Col>

          {/* Right Side Login */}
          <Col
            xs={12}
            lg={6}
            style={
              {
                // padding: "40px",
              }
            }
            className="d-flex align-items-center justify-content-center p-4 "
          >
            <Card
              className="w-100 shadow-sm border-0"
              style={{
                maxWidth: "470px",
                maxHeight: "fit-content",
                borderRadius: "20px",
                backgroundColor: "#ffffff",
                fontFamily: "Inter",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              }}
            >
              <CardBody className="p-4">
                {/* Header */}
                <div
                  style={{
                    padding: "20px 0 30px 0",
                  }}
                  className="text-center mb-4"
                >
                  <h2
                    style={{
                      fontSize: "32px",
                      fontWeight: 400,
                      color: "#050409",
                      fontFamily: "Inter",
                    }}
                  >
                    Welcome <span style={{ fontWeight: 700 }}>Back !</span>
                  </h2>
                  <p
                    style={{
                      color: "#6b7280",
                      fontSize: "16px",
                      margin: 0,
                      fontFamily: "Inter",
                    }}
                  >
                    Sign in to continue to AeraVue.
                  </p>
                </div>

                {/* Login Form */}
                {!show2fa ? (
                  <Form
                    onSubmit={e => {
                      e.preventDefault()
                      validation.handleSubmit()
                    }}
                  >
                    {error && <Alert color="danger">{error}</Alert>}

                    {/* Email */}
                    <div className="mb-3">
                      <Label
                        htmlFor="email"
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#050409",
                          fontFamily: "Inter",
                        }}
                      >
                        EMAIL ADDRESS
                      </Label>

                      {/* Wrapper with error border */}
                      <div
                        className="position-relative"
                        style={{
                          padding: "8px",
                          border:
                            validation.touched.email && validation.errors.email
                              ? "1px solid #ef4444" // red border if error
                              : "1px solid #d1d5db", // default grey
                          borderRadius: "8px",
                        }}
                      >
                        <MailOutlineIcon
                          style={{
                            position: "absolute",
                            left: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#2990DF",
                          }}
                        />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="hello@mrzul.com"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          style={{
                            paddingLeft: "44px",
                            border: "none", // remove default border
                            boxShadow: "none", // remove bootstrap shadow
                            fontFamily: "Inter",
                          }}
                        />
                      </div>

                      {/* Error text outside wrapper */}
                      {validation.touched.email && validation.errors.email && (
                        <div
                          style={{
                            color: "#ef4444",
                            fontSize: "13px",
                            marginTop: "4px",
                          }}
                        >
                          {validation.errors.email}
                        </div>
                      )}
                    </div>

                    {/* Password */}
                    <div className="mb-3">
                      <Label
                        htmlFor="password"
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#050409",
                          fontFamily: "Inter",
                        }}
                      >
                        PASSWORD
                      </Label>

                      <div
                        className="position-relative"
                        style={{
                          padding: "8px",
                          border:
                            validation.touched.password &&
                            validation.errors.password
                              ? "1px solid #ef4444"
                              : "1px solid #d1d5db",
                          borderRadius: "8px",
                        }}
                      >
                        <PasswordIcon
                          style={{
                            position: "absolute",
                            left: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#2990DF",
                          }}
                        />
                        <Input
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.password || ""}
                          style={{
                            paddingLeft: "44px",
                            paddingRight: "44px",
                            border: "none",
                            boxShadow: "none",
                            fontFamily: "Inter",
                          }}
                        />
                        <div
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "#9ca3af",
                          }}
                        >
                          {showPassword ? (
                            <VisibilityOutlinedIcon />
                          ) : (
                            <VisibilityOffOutlinedIcon />
                          )}
                        </div>
                      </div>

                      {validation.touched.password &&
                        validation.errors.password && (
                          <div
                            style={{
                              color: "#ef4444",
                              fontSize: "13px",
                              marginTop: "4px",
                            }}
                          >
                            {validation.errors.password}
                          </div>
                        )}
                    </div>

                    {/* Remember + Forgot */}
                    <Row className="align-items-center mb-3">
                      <Col xs={6}>
                        <div className="form-check">
                          <Input
                            type="checkbox"
                            id="remember"
                            className="form-check-input"
                          />
                          <Label
                            for="remember"
                            className="form-check-label secondary-color"
                            style={{
                              fontSize: "14px",
                              fontFamily: "Inter",
                            }}
                          >
                            Remember password?
                          </Label>
                        </div>
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col className="d-flex underline-link justify-content-center ">
                        <Link
                          to="/forgot-password"
                          className="primary-color"
                          style={{
                            fontSize: "14px",
                            marginTop: "20px",
                            fontWeight: 600,
                            textDecorationLine: "underline !important",
                            fontFamily: "Inter",
                          }}
                        >
                          Forgot password?
                        </Link>
                      </Col>
                    </Row>
                    <Row>
                      <Col className="d-flex justify-content-center">
                        {/* Sign In Button */}
                        <PrimaryButton
                          type="submit"
                          icon={<ArrowForwardIcon />}
                        >
                          Sign In
                        </PrimaryButton>
                      </Col>
                    </Row>
                    {/* Sign Up */}
                    {/* <div className="text-center mt-3">
                      <span
                        style={{
                          fontSize: "14px",
                          color: "#6b7280",
                          fontFamily: "Inter",
                        }}
                      >
                        Don’t have an account?{" "}
                        <Link
                          className="underline-link primary-color"
                          to="/register"
                          style={{ fontWeight: 500 }}
                        >
                          Sign Up
                        </Link>
                      </span>
                    </div> */}
                  </Form>
                ) : (
                  <Form onSubmit={handle2faSubmit}>
                    {twofaError && <Alert color="danger">{twofaError}</Alert>}
                    <div className="mb-3">
                      <Label
                        htmlFor="twofaCode"
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          fontFamily: "Inter",
                        }}
                      >
                        2FA CODE
                      </Label>
                      <Input
                        id="twofaCode"
                        type="text"
                        value={twofaCode}
                        onChange={e => setTwofaCode(e.target.value)}
                        placeholder="Enter your 2FA code"
                        style={{ fontFamily: "Inter" }}
                      />
                    </div>
                    <div
                      style={{
                        alignItems: "center",
                        justifyContent: "center",
                        display: "flex",
                      }}
                    >
                      <PrimaryButton
                        onClick={handle2faSubmit}
                        type="submit"
                        style={{}}
                      >
                        Verify 2FA
                      </PrimaryButton>
                    </div>
                  </Form>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  )
}

export default withRouter(Login)
